import React, { useMemo } from "react";
import styled from "styled-components";
import { colors2, WrapperCardGrid } from "oolib";
import {
  UI_CAPTION,
  UI_CAPTION_DF,
  UI_CAPTION_SEMIBOLD_DF,
  UI_TITLE,
} from "oolib/dist/v2/components/Typo2";
import { TopicCard } from "@/components/analysisModules/TopicClusteringTreeView/TopicCard";
import { useSignalCategories } from "@/components/analysisModules/TopicClusteringTreeView/SignalCategoriesContext";
import {
  toneColor,
  winTone,
} from "@/components/analysisModules/TopicClusteringTreeView/PrioritiesLayout/format";

/**
 * One card per signal category — the Topics card as it is: the category's
 * name and description (from the tenant's signal config), its signals as
 * the stacked bar (this period's counts, from the
 * per-signal series), and in the footer, in place of the observation
 * count, how many topics / sub-topics the scoring placed in the category
 * and how many of them are fires.
 */
// The shared grid reserves empty columns (auto-fill) — fine for a page of
// topics, wrong for four cards that should share the whole row. auto-fit
// collapses the empty tracks so the cards stretch across.
const FILL_ROW = {
  gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
};
const Foot = styled.div`
  display: flex;
  gap: 2px 14px;
  flex-wrap: wrap;
`;
// The grid plus a slim footer under it, no chrome of its own: the footer
// names what the pts on every card are measured against.
const Wrap = styled.div`
  display: flex;
  flex-direction: column;
`;
const Footnote = styled.div`
  height: 20px;
  display: flex;
  align-items: center;
  margin-top: 6px;
`;
// The category's signals on the bar, darkest first: the accent, then
// lighter steps of it.
const SHADE_ALPHA = [1, 0.62, 0.4, 0.26, 0.18];
const shade = (hex, alpha) => {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
};
const sumAt = (series, ids, key) =>
  ids.reduce((acc, id) => acc + (series[id]?.[key] || 0), 0);
const pct = (n) => `${Math.round(n * 10) / 10}%`;

// "+2.9 pts": how many points of share of all observations the category
// gained or lost since the comparison period. The colour follows the
// category's polarity: a negative one (barriers, dissatisfaction) reads red
// when rising and green when falling, a positive one (impact, satisfaction)
// the reverse, a neutral one (beliefs, responses, needs, behaviour) stays
// grey either way — a move there is news, not good or bad news. The hover
// spells out the two shares.
const ShareChange = ({ shareThen, shareNow, polarity, prevLabel }) => {
  if (shareThen === null || shareNow === null) return null;
  const moved = Math.round((shareNow - shareThen) * 10) / 10;
  const tone = moved === 0 ? "flat" : moved > 0 ? "up" : "down";
  const shown =
    polarity === "negative"
      ? tone
      : polarity === "positive"
        ? winTone(tone)
        : "flat";
  return (
    <UI_CAPTION_SEMIBOLD_DF
      title={`${pct(shareThen)} of all in ${prevLabel || "the comparison period"} → ${pct(shareNow)} now`}
      style={{
        color: shown === "flat" ? colors2.grey60 : toneColor(shown),
        whiteSpace: "nowrap",
        flexShrink: 0,
      }}
    >
      {moved > 0 ? "+" : moved < 0 ? "−" : ""}
      {Math.abs(moved).toFixed(1)} pts
    </UI_CAPTION_SEMIBOLD_DF>
  );
};

// `onOpenCategory(value)`: a card opens the category's topics (the
// Overview's side-panel stack). Optional — without it the cards are static.
export const SignalCategoryCards = ({
  scored,
  signalSeries,
  signalMetaById,
  titleByContentType,
  onOpenCategory = null,
}) => {
  const categoryTheme = useSignalCategories();
  const { priorities, wins, meta } = scored;
  const items = useMemo(() => [...priorities, ...wins], [priorities, wins]);
  const signalIds = Object.keys(signalMetaById || {});
  const totalNow = sumAt(signalSeries, signalIds, meta.nowKey);
  const totalThen = meta.compareKey
    ? sumAt(signalSeries, signalIds, meta.compareKey)
    : 0;

  return (
    <Wrap>
      <WrapperCardGrid minWidth="260px" style={FILL_ROW}>
        {categoryTheme.list.map((cat) => {
          const accent = categoryTheme.accentOf(cat.value);
          const ids = signalIds.filter(
            (id) => signalMetaById[id]?.category === cat.value,
          );
          const counted = ids
            .map((id) => ({
              key: id,
              title: signalMetaById[id]?.display || id,
              count: signalSeries[id]?.[meta.nowKey] || 0,
            }))
            .filter((seg) => seg.count > 0)
            .sort((a, b) => b.count - a.count);
          const total = counted.reduce((acc, seg) => acc + seg.count, 0);
          const then = meta.compareKey
            ? sumAt(signalSeries, ids, meta.compareKey)
            : 0;
          const shareNow = totalNow ? (total / totalNow) * 100 : null;
          const shareThen = totalThen ? (then / totalThen) * 100 : null;
          // No `count` on the segments: the legend then prints share + name only.
          const segments = counted.map(({ key, title, count }, i) => ({
            key,
            title,
            value: Math.round((count / total) * 100),
            color: shade(
              accent,
              SHADE_ALPHA[Math.min(i, SHADE_ALPHA.length - 1)],
            ),
          }));

          const inCategory = items.filter(
            (item) => item.category === cat.value,
          );
          const topics = new Set(
            inCategory.map((item) => item.bucketLabel).filter(Boolean),
          ).size;
          const fires = inCategory.filter(
            (item) => item.zone === "fires",
          ).length;
          const datasets = [
            ...new Set(inCategory.flatMap((item) => item.datasets || [])),
          ];

          return (
            <TopicCard
              key={cat.value}
              onClick={onOpenCategory ? () => onOpenCategory(cat.value) : undefined}
              title={cat.display}
              description={cat.description}
              DescriptionTypo={UI_CAPTION}
              descriptionColor={colors2.grey40}
              headerRight={
                <ShareChange
                  shareThen={shareThen}
                  shareNow={shareNow}
                  polarity={cat.polarity}
                  prevLabel={meta.prevLabel}
                />
              }
              stat={
                <div
                  style={{
                    display: "flex",
                    alignItems: "baseline",
                    gap: 8,
                    marginBottom: -6,
                  }}
                >
                  {/* Two steps above the card title (UI_BODY 14px → 16 → 20). */}
                  <UI_TITLE style={{ color: colors2.grey80 }}>
                    {total.toLocaleString()}
                  </UI_TITLE>
                  <UI_CAPTION_DF style={{ color: colors2.grey40 }}>
                    observations
                    {shareNow !== null
                      ? ` · ${Math.round(shareNow)}% of all`
                      : ""}
                  </UI_CAPTION_DF>
                </div>
              }
              segments={segments}
              footerLeft={
                <Foot>
                  <UI_CAPTION_DF style={{ color: colors2.grey40 }}>
                    {topics} topics
                  </UI_CAPTION_DF>
                  <UI_CAPTION_DF style={{ color: colors2.grey40 }}>
                    {inCategory.length} sub-topics
                  </UI_CAPTION_DF>
                  {fires > 0 && (
                    <UI_CAPTION_DF style={{ color: toneColor("up") }}>
                      {fires} in fires
                    </UI_CAPTION_DF>
                  )}
                </Foot>
              }
              datasets={datasets}
              titleByContentType={titleByContentType}
            />
          );
        })}
      </WrapperCardGrid>
      <Footnote>
        <UI_CAPTION style={{ color: colors2.grey40 }}>
          {meta.prevLabel
            ? `pts = share change ${meta.prevLabel} → ${meta.nowLabel}`
            : "pts = share change; no earlier period to compare with"}
        </UI_CAPTION>
      </Footnote>
    </Wrap>
  );
};
